import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import './TagSelector.css';

const TagSelector = ({ gameId, onChange, value = [], readOnly = false }) => {
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState(value || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // Fetch all available tags on component mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/tags');
        setTags(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching tags:', err);
        setError('Failed to load tags. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  // If gameId is provided, fetch the game's existing tags - but only once
  useEffect(() => {
    const fetchGameTags = async () => {
      if (!gameId || initialFetchDone) return;
      
      try {
        const response = await apiClient.get(`/games/${gameId}/tags`);
        setSelectedTags(response.data);
        setInitialFetchDone(true);
        
        // Also notify parent component about initial tag state
        if (onChange) {
          onChange(response.data);
        }
      } catch (err) {
        console.error(`Error fetching tags for game ${gameId}:`, err);
        // Don't set an error state here to avoid confusing the user
        setInitialFetchDone(true); // Still mark as done to prevent infinite retries
      }
    };

    fetchGameTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, initialFetchDone]); // onChange is intentionally excluded to prevent infinite loops

  // Handle tag selection
  const handleTagClick = (tag) => {
    console.log('TagSelector handleTagClick called with tag:', tag);
    if (readOnly) return;
    
    let updatedTags;
    const isSelected = selectedTags.some(t => t.id === tag.id);
    console.log('Tag is selected:', isSelected, 'Current selectedTags:', selectedTags);
    
    if (isSelected) {
      // Remove tag
      updatedTags = selectedTags.filter(t => t.id !== tag.id);
    } else {
      // Add tag
      updatedTags = [...selectedTags, tag];
    }
    
    console.log('Updated tags:', updatedTags);
    setSelectedTags(updatedTags);
    
    // Notify parent component about the change
    if (onChange) {
      console.log('Calling onChange with updatedTags:', updatedTags);
      onChange(updatedTags);
    } else {
      console.log('No onChange callback provided');
    }
  };



  // Check if a tag is selected
  const isTagSelected = (tagId) => {
    return selectedTags.some(tag => tag.id === tagId);
  };

  if (loading) {
    return <div className="tag-selector-loading">Loading tags...</div>;
  }

  if (error) {
    return <div className="tag-selector-error">{error}</div>;
  }

  const hasResults = tags.length > 0;

  return (
    <div className="tag-selector">

      
      {selectedTags.length > 0 && (
        <div className="selected-tags">
          <div className="tag-chips">
            {selectedTags.map(tag => (
              <div 
                key={tag.id} 
                className="tag-chip"
                style={{ backgroundColor: tag.color || '#6c757d' }}
                onClick={() => !readOnly && handleTagClick(tag)}
              >
                <span className="tag-chip-name">{tag.name}</span>
                {!readOnly && <span className="tag-chip-remove">×</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {!hasResults && (
        <div className="no-tags-message">
          No tags available.
        </div>
      )}
      
      {hasResults && (
        <div className="tag-list-container">
          <div className="tag-list">
            {tags.map(tag => (
              <div
                key={tag.id}
                className={`tag-item ${isTagSelected(tag.id) ? 'selected' : ''}`}
                style={{
                  backgroundColor: isTagSelected(tag.id) ? tag.color || '#6c757d' : 'transparent',
                  borderColor: tag.color || '#6c757d',
                  cursor: readOnly ? 'default' : 'pointer'
                }}
                onClick={() => handleTagClick(tag)}
              >
                <span className="tag-name">{tag.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSelector; 